/**
 * Tile Provider Configuration
 * 
 * Swappable tile provider system for satellite imagery.
 * To change providers, just update DEFAULT_PROVIDER or pass provider ID to component.
 * 
 * Similar to AI model configuration - easy to swap without code changes.
 */

export const TILE_PROVIDERS = {
  esri: {
    id: 'esri',
    name: 'Esri World Imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxZoom: 19,
    requiresApiKey: false,
    recommended: true,
    description: 'Free, high-quality satellite imagery with good racing track coverage'
  },
  
  googleSatellite: {
    id: 'googleSatellite',
    name: 'Google Satellite',
    url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    attribution: '© Google',
    maxZoom: 20,
    requiresApiKey: false,
    description: 'High-resolution Google satellite imagery (unofficial tiles)'
  },
  
  mapbox: {
    id: 'mapbox',
    name: 'Mapbox Satellite',
    url: 'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token={apiKey}',
    attribution: '© Mapbox © OpenStreetMap',
    maxZoom: 22,
    requiresApiKey: true,
    apiKeyEnvVar: 'REACT_APP_MAPBOX_TOKEN',
    description: 'Premium satellite with highest resolution (requires API key)'
  },
  
  openStreetMap: {
    id: 'openStreetMap',
    name: 'OpenStreetMap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    subdomains: ['a', 'b', 'c'],
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
    requiresApiKey: false,
    description: 'Standard map view (not satellite, useful for street names)'
  }
};

/**
 * Default tile provider
 * Change this one line to switch the default provider globally
 */
export const DEFAULT_PROVIDER = 'esri';

/**
 * Get tile provider configuration with API key injection
 * 
 * @param {string} providerId - Provider ID from TILE_PROVIDERS
 * @param {string|null} apiKey - API key (optional, will use env var if not provided)
 * @returns {Object} Provider configuration
 */
export const getTileProvider = (providerId, apiKey = null) => {
  const provider = TILE_PROVIDERS[providerId] || TILE_PROVIDERS[DEFAULT_PROVIDER];
  
  // Inject API key if required
  if (provider.requiresApiKey) {
    const key = apiKey || process.env[provider.apiKeyEnvVar];
    if (!key) {
      // Note: Using console.warn here as this is a config-level warning, not runtime error
      // eslint-disable-next-line no-console
      console.warn(`🛰️ Provider ${providerId} requires API key but none provided, falling back to ${DEFAULT_PROVIDER}`);
      return TILE_PROVIDERS[DEFAULT_PROVIDER];
    }
    return {
      ...provider,
      url: provider.url.replace('{apiKey}', key)
    };
  }
  
  return provider;
};

/**
 * Get list of available providers (filter out those requiring API keys if key not available)
 * 
 * @returns {Array} List of available provider objects
 */
export const getAvailableProviders = () => {
  return Object.values(TILE_PROVIDERS).filter(provider => {
    if (!provider.requiresApiKey) return true;
    return !!process.env[provider.apiKeyEnvVar];
  });
};

/**
 * Validate provider configuration
 * 
 * @param {Object} provider - Provider object
 * @throws {Error} If provider configuration is invalid
 */
export const validateProvider = (provider) => {
  const required = ['id', 'name', 'url', 'attribution', 'maxZoom'];
  const missing = required.filter(key => !provider[key]);
  if (missing.length > 0) {
    throw new Error(`Invalid tile provider configuration: missing ${missing.join(', ')}`);
  }
};

